import { useState, useEffect, useRef, useCallback } from "react";
import { api, getApiBaseUrl } from "../lib/utils";
import { Plane, Radio } from "lucide-react";

// Compact live ticker for the header showing real-time ingested messages
export function LiveTicker() {
  const [messages, setMessages] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pollRef = useRef(null);

  // Fetch recent messages
  const fetchRecent = useCallback(async () => {
    try {
      const res = await api.get("/api/integration/logs?limit=8");
      const data = res.data || [];
      if (data.length > 0) {
        setMessages((prev) => {
          // Merge new messages, keeping unique by id
          const existing = new Map(prev.map((m) => [m.id, m]));
          data.forEach((m) => existing.set(m.id, m));
          // Sort by timestamp desc, keep latest 10
          return Array.from(existing.values())
            .sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""))
            .slice(0, 10);
        });
      }
    } catch (e) {
      // silent
    }
  }, []);

  // WebSocket connection
  useEffect(() => {
    const connectWs = () => {
      const baseUrl = getApiBaseUrl();
      if (!baseUrl) return;
      const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
      const wsHost = baseUrl.replace(/^https?:\/\//, "");
      try {
        const ws = new WebSocket(`${wsProtocol}://${wsHost}/api/ws/dashboard`);
        wsRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          // Keepalive
          const ping = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send("ping");
          }, 25000);
          ws._ping = ping;
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === "flight_update" && msg.data) {
              const d = msg.data;
              const newMsg = {
                id: d.id || Date.now().toString(),
                msg_code: d.msg_code || "UPDATE",
                message: `${d.flight_no || "Unknown"}`,
                flight_no: d.flight_no,
                leg: d.leg,
                sender_ip: d.sender_ip,
                timestamp: d.received_at || new Date().toISOString(),
                status: "success",
              };
              setMessages((prev) => [newMsg, ...prev].slice(0, 10));
              // Trigger animation
              setAnimating(true);
              setTimeout(() => setAnimating(false), 600);
              setCurrentIdx(0);
            }
          } catch (e) {}
        };

        ws.onclose = () => {
          setConnected(false);
          if (ws._ping) clearInterval(ws._ping);
          reconnectRef.current = setTimeout(connectWs, 5000);
        };

        ws.onerror = () => {};
      } catch (e) {}
    };

    // Initial fetch + connect
    fetchRecent();
    connectWs();

    // Poll every 60s only as a fallback while the WebSocket is down
    pollRef.current = setInterval(() => { if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) fetchRecent(); }, 60000);

    return () => {
      if (wsRef.current) {
        if (wsRef.current._ping) clearInterval(wsRef.current._ping);
        wsRef.current.close();
      }
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchRecent]);

  // Cycle through messages
  useEffect(() => {
    if (messages.length <= 1) return;
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setCurrentIdx((prev) => (prev + 1) % messages.length);
        setAnimating(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [messages.length]);

  const current = messages[currentIdx];

  if (!current) {
    return (
      <div className="hidden md:flex items-center gap-1.5 text-[10px] text-muted-foreground/60 px-2 py-1 rounded bg-muted/30">
        <Radio className="h-3 w-3" />
        <span>Awaiting data...</span>
      </div>
    );
  }

  const legIcon = current.leg === "A" ? "↓" : current.leg === "D" ? "↑" : "•";
  const legColor = current.leg === "A" ? "text-green-400" : "text-blue-400";
  const timeStr = current.timestamp
    ? new Date(current.timestamp).toLocaleTimeString("en-MY", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "Asia/Kuala_Lumpur",
      })
    : "";

  return (
    <div className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/40 border border-border/50 overflow-hidden max-w-[280px] lg:max-w-[340px]">
      {/* Live pulse */}
      <div className="relative shrink-0">
        <div
          className={`w-1.5 h-1.5 rounded-full ${
            connected ? "bg-green-500" : "bg-amber-500"
          }`}
        />
        {connected && (
          <div className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-green-500 animate-ping opacity-75" />
        )}
      </div>

      {/* Plane icon */}
      <Plane
        className={`h-3 w-3 shrink-0 transition-transform duration-300 ${legColor} ${
          animating ? "scale-125 rotate-12" : ""
        }`}
      />

      {/* Message content with slide animation */}
      <div
        className={`flex items-center gap-1 text-[10px] leading-tight truncate transition-all duration-300 ${
          animating ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
        }`}
      >
        <span className={`font-semibold ${legColor}`}>{legIcon}</span>
        <span className="font-semibold text-foreground whitespace-nowrap">
          {current.flight_no || current.message}
        </span>
        <span className="text-muted-foreground font-mono">
          {current.msg_code}
        </span>
        <span className="text-muted-foreground/60 hidden lg:inline">
          {timeStr}
        </span>
      </div>

      {/* Message counter */}
      {messages.length > 1 && (
        <span className="text-[8px] text-muted-foreground/50 shrink-0 ml-auto">
          {currentIdx + 1}/{messages.length}
        </span>
      )}
    </div>
  );
}
